import type { PacketProtocol, PacketSummary } from './pcapParse';

export type PcapProtocolFilter = 'all' | 'tcp' | 'udp' | 'icmp' | 'arp' | 'other';

export interface PcapFilters {
  protocol: PcapProtocolFilter;
  address: string;
  port: string;
  streamId: number | null;
  hiddenStreamIds: number[];
}

const KNOWN: PacketProtocol[] = ['TCP', 'UDP', 'ICMP', 'ARP'];
const PROTOCOL_FILTER_ORDER: PcapProtocolFilter[] = ['all', 'tcp', 'udp', 'icmp', 'arp', 'other'];

export function filterPackets(packets: PacketSummary[], filters: PcapFilters): PacketSummary[] {
  const address = filters.address.trim().toLowerCase();
  const port = parsePort(filters.port);
  const protocol = filters.protocol;
  const streamId = filters.streamId;
  const hiddenStreamIds = new Set(filters.hiddenStreamIds);

  return packets.filter((packet) => {
    if (protocol !== 'all' && !matchesProtocol(packet.protocol, protocol)) return false;
    if (streamId != null && packet.streamId !== streamId) return false;
    if (packet.streamId != null && hiddenStreamIds.has(packet.streamId)) return false;
    if (address && !packet.src.toLowerCase().includes(address) && !packet.dst.toLowerCase().includes(address)) {
      return false;
    }
    if (port != null && packet.srcPort !== port && packet.dstPort !== port) return false;
    return true;
  });
}

export function parsePort(value: string): number | null {
  const trimmed = value.trim();
  if (!trimmed) return null;
  const port = Number(trimmed);
  if (!Number.isInteger(port) || port < 0 || port > 65535) return null;
  return port;
}

export function presentProtocolFilters(packets: PacketSummary[]): PcapProtocolFilter[] {
  const present = new Set<PcapProtocolFilter>(['all']);
  for (const packet of packets) {
    if (KNOWN.includes(packet.protocol)) {
      present.add(packet.protocol.toLowerCase() as PcapProtocolFilter);
    } else {
      present.add('other');
    }
  }
  return PROTOCOL_FILTER_ORDER.filter((value) => present.has(value));
}

function matchesProtocol(packetProtocol: PacketProtocol, filter: PcapProtocolFilter): boolean {
  if (filter === 'other') return !KNOWN.includes(packetProtocol);
  return packetProtocol.toLowerCase() === filter;
}
